import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import useTranslation from "../../../../../../packages/locales/translations/util/useTranslation";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import styles from "../media-placement-section.module.css";
import { MediaGroupedListView } from "@coworker/apprestructured/src/shared/components/MediaGroupedListView/MediaGroupedListView";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaHelperText from "@coworker/apprestructured/src/shared/wrappers/FixaHelperText/FixaHelperText";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useState, useMemo, useRef, useEffect } from "react";

interface MediaSelectionKey {
  mediaId: string;
  articleId: string;
}

interface MediaPlacementsModalProps {
  showModal: boolean;
  onClose: () => void;
  mediasAndArticleCount: MediasAndArticleCountResponse[];
  showFooter?: boolean;
  itemCardContent?: React.ReactElement;
  title?: string;
  showCheckBox?: boolean;
  itemNoAndType?: ItemNoAndType;
  taskTypeId?: string | undefined;
  mediaId?: string;
  hasMediasLoadError?: boolean;
}

const getMediaPlacementSortLabel = (
  mediaAndArticleCount: MediasAndArticleCountResponse
) =>
  mediaAndArticleCount.mediaType === MediaTypes.ROOM &&
  mediaAndArticleCount.parentMediaName
    ? mediaAndArticleCount.parentMediaName
    : mediaAndArticleCount.name;

export const MediaPlacementsModal = ({
  showModal,
  onClose,
  mediasAndArticleCount,
  showFooter = false,
  itemCardContent,
  title,
  showCheckBox = false,
  itemNoAndType,
  taskTypeId,
  mediaId,
  hasMediasLoadError = false,
}: MediaPlacementsModalProps) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParamState();
  const prevShowModalRef = useRef(showModal);

  const getInitialSelectedMedias = useMemo((): MediaSelectionKey[] => {
    if (!mediaId) return [];
    // Preselect all articles for the given mediaId
    return mediasAndArticleCount
      .filter(
        (media) =>
          media.mediaId === mediaId && !media.parentArticleCombinationNumber
      )
      .map((media) => ({
        mediaId: media.mediaId,
        articleId: media.articleId,
      }));
  }, [mediaId, mediasAndArticleCount]);

  const [selectedMediaIds, setSelectedMediaIds] = useState<MediaSelectionKey[]>(
    getInitialSelectedMedias
  );
  const [showError, setShowError] = useState(false);

  // Reset selection and error when modal reopens (transitions from false to true)
  useEffect(() => {
    if (showModal && !prevShowModalRef.current) {
      setSelectedMediaIds(getInitialSelectedMedias);
      setShowError(false);
    }
    prevShowModalRef.current = showModal;
  }, [showModal, getInitialSelectedMedias]);

  const groupByMediaType = Object.fromEntries(
    Object.entries(
      mediasAndArticleCount.reduce(
        (acc, mediaAndArticleCount) => {
          const mediaType = mediaAndArticleCount.mediaType;
          if (!acc[mediaType]) {
            acc[mediaType] = [];
          }
          acc[mediaType].push(mediaAndArticleCount);
          return acc;
        },
        {} as Record<string, MediasAndArticleCountResponse[]>
      )
    ).map(([mediaType, mediaAndArticleCounts]) => [
      mediaType,
      mediaAndArticleCounts.sort((firstMedia, secondMedia) =>
        getMediaPlacementSortLabel(firstMedia).localeCompare(
          getMediaPlacementSortLabel(secondMedia),
          undefined,
          { sensitivity: "base" }
        )
      ),
    ])
  );

  const handleConfirmClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedMediaIds.length === 0) {
      setShowError(true);
      return;
    }
    setShowError(false);

    if (!taskTypeId) return;

    setSearchParams((prev) => {
      prev.set("taskCreator", taskTypeId);
      if (itemNoAndType) {
        // Create a composite key using both mediaId and articleId
        const selectedMediasMap = new Map<
          string,
          MediasAndArticleCountResponse
        >();
        selectedMediaIds.forEach(({ mediaId: mid, articleId: aid }) => {
          const media = mediasAndArticleCount.find(
            (m) => m.mediaId === mid && m.articleId === aid
          );
          if (media) {
            // Use composite key: "mediaId:articleId"
            const compositeKey = `${mid}:${aid}`;
            selectedMediasMap.set(compositeKey, media);
          }
        });

        const selectedMedias = Array.from(selectedMediasMap.values()).map(
          (media) => ({
            id: media.mediaId,
            article_id: media.articleId,
            pieces: media.nbrArticles,
            ...(media.groupId ? { group_id: media.groupId } : {}),
          })
        );
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              item: {
                no: itemNoAndType.no,
                type: itemNoAndType.type,
              },
              medias: selectedMedias,
            },
          })
        );
      }
    });
    onClose();
  };

  const footerContent = showFooter ? (
    <FixaModalFooter>
      <div className={styles["footer-container"]}>
        {showError && (
          <FixaHelperText
            id="select-media-error"
            shouldValidate={true}
            valid={false}
            className={styles["footer-error-text"]}
            data-testid="select-media-error-message"
          >
            {t("selectMediaToContinueString", {
              defaultValue: "Select media to continue",
            })}
          </FixaHelperText>
        )}
        <FixaButton
          type="primary"
          onClick={handleConfirmClick}
          disabled={hasMediasLoadError}
        >
          {t("confirmString")}
        </FixaButton>
      </div>
    </FixaModalFooter>
  ) : null;

  const handleSelectionChange = (
    mediaId: string,
    articleId: string,
    isChecked: boolean
  ) => {
    if (isChecked) {
      setSelectedMediaIds([...selectedMediaIds, { mediaId, articleId }]);
      setShowError(false);
    } else {
      setSelectedMediaIds(
        selectedMediaIds.filter(
          (key) => !(key.mediaId === mediaId && key.articleId === articleId)
        )
      );
    }
  };

  return (
    <FixaModal visible={showModal} handleCloseBtn={onClose}>
      <FixaSheets
        labelledById="media-placements-modal-header"
        preserveAlignment={false}
        size="small"
        header={
          <FixaModalHeader
            id="media-placements-modal-header"
            title={title ?? t("allMediaPlacementsString")}
          />
        }
        footer={footerContent}
      >
        <FixaModalBody>
          {itemCardContent && itemCardContent}
          {hasMediasLoadError ? (
            <FixaInlineMessage
              variant="negative"
              title={t("couldNotLoadMediasString")}
            />
          ) : (
            <>
              <div className={styles["media-modal-list-title"]}>
                <FixaText textStyle="Heading.S">
                  {t("inFollowingMediaString")}
                </FixaText>
              </div>
              <MediaGroupedListView
                groupedMediasByType={groupByMediaType}
                showCheckbox={showCheckBox}
                selectedMediaIds={selectedMediaIds}
                onMediaSelectionChange={handleSelectionChange}
              />
            </>
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
